import React, { useEffect, useRef, useState } from 'react';

interface ImgProps {
  src: string;
  alt: string;
}

const Img: React.FC<ImgProps> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const placeholder = 'asset/images/product-placeholder-landscape.png';

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!loaded && (
        <img
          src={placeholder}
          alt="placeholder"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <img
        src={src}
        alt={alt}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: loaded ? 'block' : 'none',
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
};

const EditorialGrid: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const gridImages = [
    { src: 'asset/images/home/img3.png', alt: 'Coffee' },
    { src: 'asset/images/home/img4.png', alt: 'Jewellery' },
    { src: 'asset/images/home/img5.png', alt: 'Glasses' },
    { src: 'asset/images/home/img6.png', alt: 'Round Glasses' },
    { src: 'asset/images/home/img7.png', alt: 'Open Book' },
    { src: 'asset/images/home/img8.png', alt: 'Stacked Books' },
  ];

  return (
    <section ref={sectionRef} style={{ width: '100%', backgroundColor: '#fff', padding: '60px 0' }}>
      <style>{`
        .eg-container {
          width: 100%;
          padding: 0 100px;
          box-sizing: border-box;
        }

        /* ── Heading: fade + slide up ── */
        .eg-heading {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #111;
          margin: 0 0 24px 0;
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s ease 0.05s,
                      transform 0.7s ease 0.05s;
        }
        .eg-heading.eg--visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── Heading underline draw ── */
        .eg-heading-underline {
          display: block;
          width: 0;
          height: 2px;
          background: #717fe0;
          margin-top: 8px;
          transition: width 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.5s;
        }
        .eg-heading.eg--visible .eg-heading-underline {
          width: 48px;
        }

        /* ── Desktop: side by side ── */
        .eg-body {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        /* ── Left panel: slide from left ── */
        .eg-left {
          flex: 0 0 45%;
          position: relative;
          overflow: hidden;
          background: #f0f0f0;
          opacity: 0;
          transform: translateX(-44px);
          transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.15s,
                      transform 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.15s;
        }
        .eg-left.eg--visible {
          opacity: 1;
          transform: translateX(0);
        }

        /* ── Left image zoom on hover ── */
        .eg-left-inner {
          width: 100%;
          aspect-ratio: 1/1;
          position: relative;
          overflow: hidden;
        }
        .eg-left-zoom {
          position: absolute;
          inset: 0;
          transition: transform 0.75s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .eg-left:hover .eg-left-zoom {
          transform: scale(1.05);
        }

        /* ── Right panel: slide from right ── */
        .eg-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-width: 0;
          opacity: 0;
          transform: translateX(44px);
          transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.3s,
                      transform 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.3s;
        }
        .eg-right.eg--visible {
          opacity: 1;
          transform: translateX(0);
        }

        /* ── Grid cells: staggered fade + scale ── */
        .eg-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          width: 100%;
        }

        .eg-grid-cell {
          position: relative;
          aspect-ratio: 1/1;
          overflow: hidden;
          background: #f0f0f0;
          opacity: 0;
          transform: scale(0.92);
          transition: opacity 0.55s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.55s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .eg-grid-cell.eg--visible {
          opacity: 1;
          transform: scale(1);
        }

        /* Stagger each grid cell */
        .eg-grid-cell:nth-child(1) { transition-delay: 0.35s; }
        .eg-grid-cell:nth-child(2) { transition-delay: 0.45s; }
        .eg-grid-cell:nth-child(3) { transition-delay: 0.55s; }
        .eg-grid-cell:nth-child(4) { transition-delay: 0.65s; }
        .eg-grid-cell:nth-child(5) { transition-delay: 0.75s; }
        .eg-grid-cell:nth-child(6) { transition-delay: 0.85s; }

        /* ── Grid cell image zoom on hover ── */
        .eg-cell-zoom {
          position: absolute;
          inset: 0;
          transition: transform 0.65s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .eg-grid-cell:hover .eg-cell-zoom {
          transform: scale(1.08);
        }

        /* ── Grid cell overlay on hover ── */
        .eg-cell-overlay {
          position: absolute;
          inset: 0;
          background: rgba(113, 127, 224, 0.15);
          opacity: 0;
          transition: opacity 0.35s ease;
          z-index: 1;
          pointer-events: none;
        }
        .eg-grid-cell:hover .eg-cell-overlay {
          opacity: 1;
        }

        /* ── Caption: fade + slide up ── */
        .eg-caption {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.7s ease 0.95s,
                      transform 0.7s ease 0.95s;
        }
        .eg-caption.eg--visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── Caption title underline draw ── */
        .eg-caption-title {
          display: inline-block;
          position: relative;
        }
        .eg-caption-title::after {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 0;
          width: 0;
          height: 1.5px;
          background: #222;
          transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1) 1.4s;
        }
        .eg-caption.eg--visible .eg-caption-title::after {
          width: 100%;
        }

        /* ── Tablet / small screens ── */
        @media (max-width: 768px) {
          .eg-body {
            flex-direction: column;
            gap: 16px;
          }
          .eg-left {
            flex: none;
            width: 100%;
          }
          .eg-left-inner {
            aspect-ratio: 16/9;
          }
          .eg-right {
            width: 100%;
          }
          .eg-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
          }
        }

        /* ── Mobile ── */
        @media (max-width: 480px) {
          .eg-container {
            padding: 0 20px;
          }
          .eg-grid {
            gap: 6px;
          }
          .eg-heading {
            font-size: 18px;
          }
        }
      `}</style>

      <div className="eg-container">

        {/* Heading */}
        <h2 className={`eg-heading ${visible ? 'eg--visible' : ''}`}>
          Kryptonian Labs Editorial
          <span className="eg-heading-underline" />
        </h2>

        <div className="eg-body">

          {/* ── Left large image ── */}
          <div className={`eg-left ${visible ? 'eg--visible' : ''}`}>
            <div className="eg-left-inner">
              <div className="eg-left-zoom">
                <Img src="asset/images/home/img10.png" alt="Editorial Model" />
              </div>
            </div>
          </div>

          {/* ── Right: 3×2 grid + caption ── */}
          <div className={`eg-right ${visible ? 'eg--visible' : ''}`}>
            <div className="eg-grid">
              {gridImages.map((img, i) => (
                <div key={i} className={`eg-grid-cell ${visible ? 'eg--visible' : ''}`}>
                  <div className="eg-cell-zoom">
                    <Img src={img.src} alt={img.alt} />
                  </div>
                  <div className="eg-cell-overlay" />
                </div>
              ))}
            </div>

            <div className={`eg-caption ${visible ? 'eg--visible' : ''}`}>
              <span className="eg-caption-title" style={{ display: 'block', fontWeight: 700, fontSize: '20px', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                Stories &amp; Style |
              </span>
              <span style={{ display: 'block', fontSize: '17px' }}>
                Beyond the rack, into the world.
              </span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default EditorialGrid;